import { createContext, useContext, useEffect, useMemo } from 'react'
import { type IntlShape, type MessageDescriptor, useIntl } from 'react-intl'

import { useLocation } from '@tanstack/react-router'

import { type SupportPageContext, useSupportPanelStore } from '@/state/support/supportPanelStore'

type MessageId = NonNullable<MessageDescriptor['id']>

export interface SupportPageOverride {
  readonly page: string
  readonly object: { label: string; url: string } | null
}

export const SupportPageOverrideContext = createContext<SupportPageOverride | null>(null)

const PAGE_BY_PATH: Array<[RegExp, MessageId]> = [
  [/^\/$/, 'soutien.page.operations'],
  [/\/messages(\/|$)/, 'soutien.page.messages'],
  [/^\/assignments(\/|$)/, 'soutien.page.assignments'],
  [/^\/promotions(\/|$)/, 'soutien.page.promotions'],
  [/^\/judicial(\/|$)/, 'soutien.page.judicial'],
  [/^\/stats(\/|$)/, 'soutien.page.stats'],
  [/^\/regions(\/|$)/, 'soutien.page.administration'],
]

const OBJECT_BY_PATH: Array<[RegExp, MessageId]> = [
  [/^\/judicial\/([^/]+)/, 'soutien.page.object.judicialRecord'],
  [/^\/assignments\/([^/]+)/, 'soutien.page.object.assignmentPlan'],
  [/^\/promotions(?:\/[^/]+){2}\/(?!create$)([^/]+)$/, 'soutien.page.object.promotion'],
]

const BROWSERS: Array<[string, RegExp]> = [
  ['Edge', /Edg\/(\d+)/],
  ['Opera', /OPR\/(\d+)/],
  ['Firefox', /Firefox\/(\d+)/],
  ['Chrome', /Chrome\/(\d+)/],
  ['Safari', /Version\/(\d+)[\d.]*(?: Mobile\/\S+)? Safari/],
]

const OPERATING_SYSTEMS: Array<[string, RegExp]> = [
  ['iPadOS', /iPad/],
  ['iOS', /iPhone|iPod/],
  ['Android', /Android/],
  ['Windows', /Windows/],
  ['ChromeOS', /CrOS/],
  ['macOS', /Mac OS X|Macintosh/],
  ['Linux', /Linux/],
]

export const describeSystem = (userAgent: string) => {
  const operatingSystem = OPERATING_SYSTEMS.find(([, pattern]) => pattern.test(userAgent))?.[0]
  const browser = BROWSERS.map(([name, pattern]) => [name, pattern.exec(userAgent)?.[1]] as const).find(
    ([, version]) => version !== undefined
  )
  return [operatingSystem, browser && `${browser[0]} ${browser[1]}`].filter(Boolean).join(' · ')
}

export const useRegisterSupportPage = ({ detail, object }: SupportPageContext) => {
  const setPageContext = useSupportPanelStore((state) => state.setPageContext)
  const label = object?.label
  const url = object?.url

  useEffect(() => {
    setPageContext({ detail, object: label && url ? { label, url } : null })
    return () => setPageContext({ detail: null, object: null })
  }, [setPageContext, detail, label, url])
}

interface SupportPageLocation {
  readonly pathname: string
  readonly href: string
  readonly title: string
}

export const supportPageOf = (
  intl: IntlShape,
  { pathname, href, title }: SupportPageLocation,
  pageContext: SupportPageContext
) => {
  const pageId = PAGE_BY_PATH.find(([pattern]) => pattern.test(pathname))?.[1]
  const routeObject = OBJECT_BY_PATH.map(([pattern, id]) => [id, pattern.exec(pathname)?.[1]] as const).find(
    ([, value]) => value !== undefined
  )
  const object =
    pageContext.object ?? (routeObject?.[1] ? { label: decodeURIComponent(routeObject[1]), url: href } : null)
  const detail =
    pageContext.detail ?? (routeObject?.[1] ? intl.formatMessage({ id: routeObject[0] }, { id: object?.label }) : null)

  const page = [pageId ? intl.formatMessage({ id: pageId }) : title, detail].filter(Boolean).join(' › ')
  return { page, object }
}

export const useSupportContext = () => {
  const intl = useIntl()
  const pathname = useLocation({ select: (location) => location.pathname })
  const pageContext = useSupportPanelStore((state) => state.pageContext)
  const override = useContext(SupportPageOverrideContext)

  return useMemo(() => {
    if (override) {
      return { page: override.page, object: override.object, system: describeSystem(navigator.userAgent) }
    }
    const { page, object } = supportPageOf(
      intl,
      { pathname, href: globalThis.location.href, title: document.title },
      pageContext
    )
    return { page, object, system: describeSystem(navigator.userAgent) }
  }, [intl, pathname, pageContext, override])
}
